<template>
  <h2>Messen aus KaPlan importieren</h2>
  <div v-if="this.masses.length === 0">
    <form class="row row-cols-3 g-3">
      <div class="col">
        <label class="form-label" for="startDate">Start-Datum</label>
        <input id="startDate" v-model="startDate" class="form-control" required type="date">
      </div>
      <div class="col">
        <label class="form-label" for="endDate">End-Datum</label>
        <input id="endDate" v-model="endDate" class="form-control" type="date">
      </div>
      <div class="col">
        <label class="form-label" for="churchId">Kirchstandort</label>
        <select id="churchId" v-model="churchId" class="form-select" required>
          <option v-for="parish in this.parishes" :key="parish.value" :value="parish.value">{{ parish.name }}</option>
        </select>
      </div>
      <div class="col">
        <button type="button" class="btn btn-primary" @click="this.loadMasses"><em class="align-middle material-icons">refresh</em>Aus
          KaPlan laden...
        </button>
      </div>
    </form>
  </div>
  <div v-if="this.masses.length > 0">
    <form class="row row-cols-3 g-3">
      <div class="col">
        <label class="form-label" for="requiredAltarServers">Anz. Messdiener</label>
        <input id="requiredAltarServers" v-model="massBulkData.requiredAltarServers" class="form-control"
               required type="number">
      </div>
      <div class="col">
        <label class="form-label" for="requiredLeaders">Anz. Leiter</label>
        <input id="requiredLeaders" v-model="massBulkData.requiredLeaders" class="form-control" required
               type="number">
      </div>
      <div class="col">
        <div class="form-check">
          <label class="form-label" for="incense">Weihrauch?</label>
          <input id="incense" v-model="massBulkData.incense" class="form-check-input" required type="checkbox">
        </div>
      </div>
      <div class="col">
        <div class="form-check">
          <label class="form-check-label" for="boldTime">Uhrzeit dick?</label>
          <input id="boldTime" v-model="massBulkData.boldTime" class="form-check-input" type="checkbox">
        </div>
      </div>
      <div class="col">
        <label class="form-label" for="annotation">Anmerkung</label>
        <input id="annotation" v-model="massBulkData.annotation" class="form-control" type="text">
      </div>
      <div class="col">
        <button type="button" class="btn btn-warning" @click="updateFilteredMasses">Aktuell angezeigte Messen anpassen</button>
      </div>
    </form>
    <h3>Filter</h3>
    <multiselect v-model="this.shownLocations" :multiple="true"
                 :options="this.locations"></multiselect>
    <multiselect v-model="this.shownDoWs"
                 :custom-label="this.dayOfWeek" :multiple="true" :options="[1, 2, 3, 4, 5, 6, 0]"></multiselect>
    <h3>Messen</h3>
    <button class="btn btn-success" @click="bulkAdd">Ausgewählte Messen importieren</button>
    <button class="btn btn-primary" @click="toggleSelected(true)">Alle angezeigten für Import auswählen</button>
    <button class="btn btn-primary" @click="toggleSelected(false)">Alle angezeigten von Import ausschließen</button>
    <DataTable :cols="[
          {title: '', key: 'import', type: 'checkbox'},
          {title: 'Datum & Uhrzeit', key: 'datetime', type: 'datetime'},
          {title: 'Ort', key: 'location', type: 'text'},
          {title: 'Beschreibung', key: 'description', type: 'text'},
          {title: 'Priester', key: 'priest', type: 'text'},
          {title: 'Weihrauch', key: 'incense', type: 'checkbox', alignment: 'center'},
          {title: 'Uhrzeit dick?', key: 'boldTime', type: 'checkbox', alignment: 'center'},
          {title: 'Anz. Messdiener', key: 'requiredAltarServers', type: 'number'},
          {title: 'Anz. Leiter', key: 'requiredLeaders', type: 'number'},
          {title: 'Anmerkung', key: 'annotation', type: 'text'},
      ]" :data="this.shownMasses"></DataTable>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import {useMassStore} from "@/stores/mass";
import DataTable from "@/components/DataTable.vue";

export default {
  name: "MassKaPlanImport",
  components: {DataTable, Multiselect},
  props: ['triggerIcon', 'triggerText', 'triggerClass'],
  data: function () {
    return {
      startDate: new Date().toISOString().split('T')[0],
      endDate: null,
      churchId: 0,
      parishes: [],
      masses: [],
      locations: [],
      shownLocations: [],
      shownDoWs: [],
      massBulkData: {},
    }
  },
  computed: {
    shownMasses() {
      return this.masses.filter(m => this.shownLocations.includes(m.location) && this.shownDoWs.includes(new Date(m.datetime).getDay())).sort((a, b) => a.datetime - b.datetime);
    }
  },
  methods: {
    toggleSelected(status) {
      for (let mass of this.shownMasses)
        mass['import'] = status;
    },
    async loadMasses() {
      let url = `/kaplan/masses/?kgid=${this.churchId}&start_date=${this.startDate}`;
      if (this.endDate) {
        url += `&end_date=${this.endDate}`;
      }
      try {
        const response = await this.axios.get(url);
        this.masses = response.data;
        for (let mass of this.masses) {
          mass['import'] = false;
          mass['incense'] = false;
          mass['boldTime'] = false;
          mass['requiredAltarServers'] = 0;
          mass['requiredLeaders'] = 0;
          mass['annotation'] = '';
          if (!this.locations.includes(mass['location'])) {
            this.locations.push(mass['location']);
            this.shownLocations.push(mass['location']);
          }
        }
      } catch (e) {
        console.log(e);
      }
    },
    updateFilteredMasses() {
      for (let mass of this.shownMasses)
        for (const [key, value] of Object.entries(this.massBulkData))
          mass[key] = value;
    },
    bulkAdd() {
      for (let mass of this.masses)
        if (mass.import) {
          this.massStore.addItem(mass);
          mass.import = false;
        }
    },
    dayOfWeek(index) {
      let dows = ["SO", "MO", "DI", "MI", "DO", "FR", "SA"];
      return dows[index];
    }
  },
  async created() {
    this.startDate = new Date().toISOString().split('T')[0];
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 30);
    this.endDate = endDate.toISOString().split('T')[0];
    this.churchId = 0;
    this.massBulkData = {};
    this.masses = [];
    this.locations = [];
    this.shownLocations = [];
    this.shownDoWs = [6, 0]
    try {
      const response = await this.axios.get('/kaplan/parishes/')
      this.parishes = response.data;
    } catch (e) {
      console.log(e);
    }
    if (!this.massStore.loadedData)
      await this.massStore.getData();
  },
  setup() {
    const massStore = useMassStore();
    return {massStore};
  }
}
</script>
